import { useCallback, useMemo, useState, useRef, useEffect } from 'react'
import ReactPlayer, { ReactPlayerProps } from 'react-player'
import useFullscreen from 'react-use/lib/useFullscreen'
import useIdle from 'react-use/lib/useIdle'
import useToggle from 'react-use/lib/useToggle'

import { formatTime, toPlaybackRateOption, toQualityOption } from './utils'
import {
  INITIAL_SAVED_CURRENT_TIME,
  PLAYBACK_RATE,
  QUALITIES,
  PLAYBACK_RATE_OPTION,
} from './constants'

type Option = {
  label: string
  value: string | number
}

type Time = {
  label: string
  value: number
}

type Source = {
  quality: number
  src: string
}

type DefaultValues = {
  isMuted?: boolean
  playbackRate?: PLAYBACK_RATE
  isPlaying?: boolean
  quality?: QUALITIES
  isShowingTracks?: boolean
  volume?: number
}

export type Arguments = DefaultValues &
  Pick<ReactPlayerProps, 'onStart' | 'onEnded'> & {
    onSubtitleClick?: React.MouseEventHandler<HTMLButtonElement>
    onMuteClick?: React.MouseEventHandler<HTMLButtonElement>
    onPlaybackRateChange?: (playbackRate: PLAYBACK_RATE) => void
    onQualityChange?: (qualities: QUALITIES) => void
    onVolumeChange?: (value: string) => void
    sources: Source[]
  }

const initialTime: Time = {
  label: formatTime(0),
  value: 0,
}

export const usePlayer = ({
  onSubtitleClick: onEnhancedSubtitleClick,
  onMuteClick: onEnhancedMuteClick,
  onPlaybackRateChange: onEnhancedPlaybackRateChange,
  onQualityChange: onEnhancedQualityChange,
  onStart: onEnhancedStart,
  onEnded: onEnhancedEnded,
  onVolumeChange: onEnhancedVolumeChange,
  sources,
  ...props
}: Arguments) => {
  const videoRef = useRef<ReactPlayer>(null)

  // 動画を囲む要素へのref (フルスクリーンで使用)
  const wrapperRef = useRef<HTMLDivElement>(null)

  // 再生・ポーズ
  const [isPlaying, onPlayClick] = useToggle(props.isPlaying ?? false)

  // ミュート
  const [isMuted, setMuted] = useToggle(props.isMuted ?? false)

  // 字幕表示のON/OFFの切り替え
  const [isShowingTracks, setShowingTracks] = useToggle(
    props.isShowingTracks ?? false
  )

  // 音量
  const [volume, setVolume] = useState<number>(props.volume ?? 1)

  // 再生時間 (現在の再生位置)
  const [currentTime, setCurrentTime] = useState<Time>(initialTime)

  /*
    現在の動画の再生位置を記憶させるためのstate
    動画品質切り替えの後に0秒から再生されてしまうのを防ぎ、元の再生位置に再度シークし直すために使用する。
  */
  const [savedCurrentTime, setSavedCurrentTime] = useState<number>(
    INITIAL_SAVED_CURRENT_TIME
  )

  // 総合時間
  const [duration, setDuration] = useState<Time>(initialTime)

  // 再生速度倍率
  const [playbackRate, setPlaybackRate] = useState<Option>(
    props.playbackRate
      ? toPlaybackRateOption(props.playbackRate)
      : PLAYBACK_RATE_OPTION[1]
  )

  // 動画品質
  const [currentQuality, setCurrentQuality] = useState<Option>(
    toQualityOption(props.quality || 360)
  )

  // 動画品質切り替え後かどうか(動画品質切り替え時からonStartイベント発火時までの間にtrueになる)
  const [hasChangedQuality, setHasChangedQuality] = useState<boolean>(false)

  // 動画ロード時のHandler
  const onReady = useCallback<(savedCurrentTime: number) => void>(
    _savedCurrentTime => {
      if (!videoRef.current) {
        return
      }

      // 保存された再生位置にシークさせる
      if (_savedCurrentTime > INITIAL_SAVED_CURRENT_TIME) {
        videoRef.current.seekTo(_savedCurrentTime, 'seconds')
        setSavedCurrentTime(INITIAL_SAVED_CURRENT_TIME)
      }
    },
    [videoRef, setSavedCurrentTime]
  )

  // 動画の初期再生時のHandler (Called when media starts playing)
  const onStart = useCallback<(hasChangedQuality: boolean) => void>(
    _hasChangedQuality => {
      // 動画品質切替時にもイベントが発火するため、一番最初の再生時以外は実行しない
      if (!_hasChangedQuality && onEnhancedStart) {
        onEnhancedStart()
      } else {
        setHasChangedQuality(false)
      }
    },
    [onEnhancedStart, setHasChangedQuality]
  )

  // 動画終了時のHandler
  const onEnded = useCallback<NonNullable<ReactPlayerProps['onEnded']>>(() => {
    if (onEnhancedEnded) {
      onEnhancedEnded()
    }
  }, [onEnhancedEnded])

  // 再生中の変更用Handler
  const onProgress = useCallback<NonNullable<ReactPlayerProps['onProgress']>>(
    ({ playedSeconds }) =>
      setCurrentTime({
        label: formatTime(playedSeconds),
        value: playedSeconds,
      }),
    [setCurrentTime]
  )

  // 総合時間の変更用Handler
  const onDuration = useCallback<NonNullable<ReactPlayerProps['onDuration']>>(
    (newDuration: number) =>
      setDuration({
        label: formatTime(newDuration),
        value: newDuration,
      }),
    [setDuration]
  )

  // 再生位置の変更用Handler
  const onSeek = useCallback<React.ChangeEventHandler<HTMLInputElement>>(
    e => {
      if (!videoRef.current) {
        return
      }

      const time = Number(e.currentTarget.value)
      videoRef.current.seekTo(time, 'seconds')
    },
    [videoRef]
  )

  // 音量の変更用Handler
  const onVolumeChange = useCallback<
    React.ChangeEventHandler<HTMLInputElement>
  >(
    e => {
      const newVolume = Number(e.currentTarget.value)

      setMuted(newVolume === 0)
      setVolume(newVolume)

      if (onEnhancedVolumeChange) {
        onEnhancedVolumeChange(e.currentTarget.value)
      }
    },
    [setMuted, setVolume, onEnhancedVolumeChange]
  )

  // ミュート状態の変更用Handler
  const onMuteClick = useCallback<React.MouseEventHandler<HTMLButtonElement>>(
    e => {
      setMuted()

      if (onEnhancedMuteClick) {
        onEnhancedMuteClick(e)
      }
    },
    [setMuted, onEnhancedMuteClick]
  )

  // 字幕表示の変更用Handler
  const onSubtitleClick = useCallback<
    React.MouseEventHandler<HTMLButtonElement>
  >(
    e => {
      setShowingTracks()

      if (onEnhancedSubtitleClick) {
        onEnhancedSubtitleClick(e)
      }
    },
    [setShowingTracks, onEnhancedSubtitleClick]
  )

  // 再生速度倍率の変更用handler
  const onPlaybackRateChange = useCallback<(option: Option) => void>(
    option => {
      setPlaybackRate(option)

      if (onEnhancedPlaybackRateChange) {
        onEnhancedPlaybackRateChange(option.value as PLAYBACK_RATE)
      }
    },
    [setPlaybackRate, onEnhancedPlaybackRateChange]
  )

  // 動画品質の変更用handler
  const onQualityChange = useCallback<(option: Option) => void>(
    option => {
      if (videoRef.current) {
        setSavedCurrentTime(videoRef.current.getCurrentTime())
      }
      setCurrentQuality(option)
      setHasChangedQuality(true)

      if (onEnhancedQualityChange) {
        onEnhancedQualityChange(option.value as QUALITIES)
      }
    },
    [
      videoRef,
      setSavedCurrentTime,
      setCurrentQuality,
      setHasChangedQuality,
      onEnhancedQualityChange,
    ]
  )

  const [fullscreenOn, onFullscreenChange] = useToggle(false)
  const internalVideoRef = useMemo(
    () =>
      videoRef.current
        ? { current: videoRef.current.getInternalPlayer() }
        : { current: null },
    // currentプロパティを監視しないと再計算されないため
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [videoRef.current]
  ) as React.RefObject<HTMLVideoElement>

  const isFullscreen = useFullscreen(wrapperRef, fullscreenOn, {
    onClose: () => onFullscreenChange(false),
    video: internalVideoRef,
  })

  // ユーザーが操作終了後、一定時間が経過したか? (コントローラーの表示・非表示の制御等に利用する)
  const isUserIdle = useIdle(3e3)

  // 動画上での右クリックでのメニュー表示を抑制
  const onContextMenu = useCallback<React.MouseEventHandler<HTMLVideoElement>>(
    e => e.preventDefault(),
    []
  )

  const currentSource = useMemo<Source | undefined>(
    () => sources.find(source => source.quality === currentQuality.value),
    [sources, currentQuality.value]
  )

  if (!currentSource) {
    throw new Error('There is no specified source')
  }

  useEffect(() => {
    const { current } = internalVideoRef
    if (!current || current.textTracks.length === 0) return

    // TODO:字幕が複数言語に対応した際に要対応
    const textTrack = current.textTracks[0]
    textTrack.mode = isShowingTracks ? 'showing' : 'disabled'
  }, [internalVideoRef, isShowingTracks])

  return {
    currentQuality,
    currentSource,
    currentTime,
    duration,
    hasChangedQuality,
    internalVideoRef,
    isFullscreen,
    isMuted,
    isPlaying,
    isShowingTracks,
    isUserIdle,
    onSubtitleClick,
    onContextMenu,
    onDuration,
    onEnded,
    onFullscreenChange,
    onMuteClick,
    onPlayClick,
    onPlaybackRateChange,
    onProgress,
    onQualityChange,
    onReady,
    onSeek,
    onStart,
    onVolumeChange,
    playbackRate,
    savedCurrentTime,
    videoRef,
    volume,
    wrapperRef,
  }
}
